import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../media/Transparent-Star-Only-Logo.png';

// Homepage sections the footer links to. Off the homepage we navigate there with
// ?section=<id> and LandingPageNew scrolls to it on load.
const HOMEPAGE_SECTIONS = [
  { name: 'How it Works?', id: 'how-it-works' },
  { name: 'Pricing', id: 'pricing' },
  { name: 'FAQs', id: 'faqs' },
  { name: 'Reviews', id: 'reviews' },
];

const linkClass =
  'block text-white text-base hover:text-[#BAE066] transition-colors duration-200 text-left';
const poppins = { fontFamily: 'Poppins, sans-serif' };

/** Shared public-site footer (homepage + About page). */
const SiteFooter = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const goToSection = (sectionId) => {
    if (location.pathname === '/') {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/?section=' + sectionId);
    }
  };

  return (
    <footer id="contact-us" className="bg-[#2F1E6B] w-full">
      <div className="px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">

          {/* Left Section - Logo and Description */}
          <div className="flex-1 lg:max-w-md">
            <div className="mb-6">
              <img src={Logo} alt="Nova Learning" className="h-12 w-auto" />
            </div>
            <p className="text-white text-base mb-6 leading-relaxed" style={poppins}>
              Interactive maths platform for Years 2-6 with AI tutor Addy and real teachers. No more boring worksheets - just fun learning adventures!
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=61565935770588"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Nova Learning on Facebook"
                className="w-10 h-10 bg-[#BAE066] rounded-full flex items-center justify-center hover:bg-[#A7D73C] transition-colors duration-200"
              >
                <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/novalearninguk/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Nova Learning on Instagram"
                className="w-10 h-10 bg-[#BAE066] rounded-full flex items-center justify-center hover:bg-[#A7D73C] transition-colors duration-200"
              >
                <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.40s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Middle Section - Programs */}
          <div className="flex-1 lg:max-w-xs">
            <h3 className="text-white text-xl font-semibold mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Programs
            </h3>
            <div className="space-y-3">
              {HOMEPAGE_SECTIONS.map((section) => (
                <button
                  key={section.id}
                  onClick={() => goToSection(section.id)}
                  className={linkClass}
                  style={poppins}
                >
                  {section.name}
                </button>
              ))}
              <Link to="/about" className={linkClass} style={poppins}>
                About us
              </Link>
              <a href="https://blog.novalearning.co.uk" className={linkClass} style={poppins}>
                Blog
              </a>
            </div>
          </div>

          {/* Right Section - Contact */}
          <div className="flex-1 lg:max-w-xs">
            <h3 className="text-white text-xl font-semibold mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Contact
            </h3>
            <div className="space-y-3">
              <div className="text-white" style={poppins}>
                <span className="font-semibold">Email:</span> info@novalearning.co.uk
              </div>
              <div className="text-white" style={poppins}>
                <span className="font-semibold">Phone:</span> +447438840849
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Copyright Section */}
      <div className="border-t border-white/20 px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 py-4">
        <div className="text-center">
          <p className="text-white text-sm" style={poppins}>
            © {new Date().getFullYear()} Nova. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
